import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type AdminUpdateFeedbackRequest,
  type AgentFeedbackRecord,
} from "@/shared/domain/agentThread";
import { PaginatedResponse } from "@/shared/http";

import type {
  AdminService,
  FeedbackCountsBySession,
  GetAdminFeedbackOptions,
} from "../AdminService";

// Derived from the service shape so the two cannot drift. The hook supplies
// ``abortSignal`` itself from the query's signal, so callers should not pass
// one through the filters.
export type AdminFeedbackFilters = Omit<GetAdminFeedbackOptions, "abortSignal">;

export function useAdminFeedback(filters: AdminFeedbackFilters) {
  const { adminService } = useDomainServices();

  return useQuery<PaginatedResponse<AgentFeedbackRecord>, Error>({
    // The whole filter object, so a new filter field joins the key on its own.
    queryKey: ["adminFeedback", filters],
    queryFn: ({ signal }) =>
      adminService.getAdminFeedback({ ...filters, abortSignal: signal }),
    staleTime: 30000,
  });
}

export function useAdminFeedbackCountsForAgentThreads(filters: {
  orgId?: string;
  createdBy?: string;
  sessionId?: string;
}) {
  const { adminService } = useDomainServices();

  // ``||`` (truthy chain) rather than ``??`` (nullish-only) so an empty
  // string from a cleared-filter UI state correctly resolves to "no filter
  // set" — ``"" ?? next`` short-circuits on the empty string and would
  // mistakenly enable the query with no real filter, which the service
  // method then rejects.
  const enabled = Boolean(
    filters.orgId || filters.createdBy || filters.sessionId,
  );

  return useQuery<FeedbackCountsBySession, Error>({
    queryKey: [
      "adminFeedbackCounts",
      filters.orgId,
      filters.createdBy,
      filters.sessionId,
    ],
    queryFn: ({ signal }) =>
      adminService.getAdminFeedbackCountsForAgentThreads(filters, {
        abortSignal: signal,
      }),
    enabled,
    staleTime: 30000,
  });
}

/**
 * Query for the admin triage labels in use, most-used first, optionally only
 * those containing ``contains``. A factory rather than only a hook so code
 * outside a component (a filter's option resolver) can share the hook's cache
 * entry through ``queryClient.fetchQuery``. A triage save invalidates it (see
 * ``useUpdateAdminFeedback``).
 */
export function adminFeedbackLabelsQueryOptions(
  adminService: AdminService,
  contains = "",
) {
  return queryOptions({
    queryKey: ["adminFeedbackLabels", contains],
    queryFn: ({ signal }) =>
      adminService.getAdminFeedbackLabels({ abortSignal: signal, contains }),
    staleTime: 30000,
  });
}

/** Admin triage labels in use, for the triage form's label suggestions. */
export function useAdminFeedbackLabels() {
  const { adminService } = useDomainServices();
  return useQuery(adminFeedbackLabelsQueryOptions(adminService));
}

interface UpdateAdminFeedbackParams {
  feedbackId: string;
  request: AdminUpdateFeedbackRequest;
}

/**
 * Admin mutation for triage fields (label/note/resolved).
 *
 * On success, invalidates the feedback listing, the per-thread counts, and the
 * label suggestions (a save can add a label or retire the last use of one), so
 * those views reflect the save without a manual refresh. A caller holding its
 * own copy of the record must update it from the mutation's result.
 */
export function useUpdateAdminFeedback() {
  const { adminService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<AgentFeedbackRecord, Error, UpdateAdminFeedbackParams>({
    mutationFn: async ({ feedbackId, request }: UpdateAdminFeedbackParams) =>
      adminService.updateAdminFeedback(feedbackId, request),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["adminFeedback"] }),
        queryClient.invalidateQueries({ queryKey: ["adminFeedbackCounts"] }),
        queryClient.invalidateQueries({ queryKey: ["adminFeedbackLabels"] }),
      ]);
    },
  });
}
